import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaCircle } from 'react-icons/fa';

const DemoSection = styled.section`
  padding: 6rem 1.5rem;
  background: ${props => props.theme.colors.background};
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 968px) {
    flex-direction: column;
    gap: 3rem;
    padding: 0 1rem;
  }
`;

const Content = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 800;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: clamp(1.75rem, 4vw, 2rem);
  }
`;

const Description = styled(motion.p)`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.7;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const WindowWrapper = styled(motion.div)`
  flex: 1;
  background: ${props => props.theme.isDark ? '#0f172a' : '#1e293b'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px ${props => props.theme.isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  position: relative;
  z-index: 2;
  
  @media (max-width: 968px) {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const WindowHeader = styled.div`
  background: ${props => props.theme.isDark ? '#1e293b' : '#334155'};
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
`;

const WindowButton = styled(FaCircle)`
  font-size: 0.75rem;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  
  &:nth-child(1) { color: #ef4444; }
  &:nth-child(2) { color: #fbbf24; }
  &:nth-child(3) { color: #22c55e; }
  
  &:hover {
    opacity: 1;
  }
`;

const WindowContent = styled.div`
  position: relative;
  padding-top: 56.25%; // 16:9 aspect ratio
  background: #0f172a;
  overflow: hidden;

  video, img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(15, 23, 42, 0.1),
      rgba(15, 23, 42, 0.05)
    );
    pointer-events: none;
  }
`;

const LoadingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.isDark ? '#1e293b' : '#334155'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
`;

const ProductDemo = () => {
  return (
    <DemoSection>
      <Container>
        <Content>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            Streamline Your Service Management
          </Title>
          <Description
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
          >
            Experience the power of modern service management with our intuitive platform. 
            Automate workflows, track performance, and deliver exceptional service—all in 
            one place.
          </Description>
        </Content>

        <WindowWrapper
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
        >
          <WindowHeader>
            <WindowButton />
            <WindowButton />
            <WindowButton />
          </WindowHeader>
          <WindowContent>
            <video
              autoPlay
              muted
              loop
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            >
              <source src="/compressed-bro.mp4" type="video/mp4" />
            </video>
          </WindowContent>
        </WindowWrapper>
      </Container>
    </DemoSection>
  );
};

export default ProductDemo;
